import { useState, useEffect } from 'react'
import { Smile, Meh, Frown, Loader2, HelpCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoSentiment } from '../utils/demoData'
import TrendModal from './TrendModal'
import TrendBadge from './TrendBadge'

// Tooltip component
const Tooltip = ({ text, children }: { text: string; children: React.ReactNode }) => {
  const [show, setShow] = useState(false)

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
            {text}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function SentimentCard() {
  const { activeBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [loading, setLoading] = useState(true)
  const [allSentimentData, setAllSentimentData] = useState<any[]>([])
  const [showTrendModal, setShowTrendModal] = useState(false)

  useEffect(() => {
    fetchSentiment()
  }, [activeBrand?.id, isDemoMode])

  const fetchSentiment = async () => {
    if (!activeBrand?.id && !isDemoMode) return

    try {
      setLoading(true)

      // Use demo data if in demo mode
      if (isDemoMode) {
        const data = demoSentiment.data
        setAllSentimentData(data)
        setLoading(false)
        return
      }

      const response = await api.get(`/competitors/${activeBrand!.id}/sentiment`)

      if (response.data.success && response.data.data) {
        const data = response.data.data
        setAllSentimentData(data)
      }
    } catch (error) {
      console.error('Failed to fetch Sentiment:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSentimentColor = (score: number) => {
    if (score >= 70) return 'text-green-600'
    if (score >= 40) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getSentimentLabel = (score: number) => {
    if (score >= 70) return 'Positive'
    if (score >= 40) return 'Mixed'
    return 'Needs Attention'
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Smile className="h-5 w-5 text-green-600" />
            <h3 className="text-sm font-medium text-gray-600">Sentiment Score</h3>
          </div>
        </div>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
        </div>
      </div>
    )
  }

  // Check if there's any actual data (any brand with mentions)
  const hasData = allSentimentData.length > 0 && allSentimentData.some((brand: any) => brand.totalMentions > 0)

  // Get your brand's data (may not have mentions) - needed for trendData
  const yourBrand = allSentimentData.find((b: any) => b.isYourBrand)
  const score = yourBrand?.averageScore || 0

  // Trend data (6 months) - only use demo values in demo mode
  const trendData = isDemoMode ? [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 68 },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 70 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 72 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 74 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 76 },
    { date: new Date(Date.now()).toISOString(), value: score || 78 }
  ] : [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: 0 },
    { date: new Date(Date.now()).toISOString(), value: score }
  ]

  if (!hasData) {
    return (
      <>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Smile className="h-5 w-5 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-600">Sentiment Analysis</h3>
            </div>
            <TrendBadge
              hasHistoricalData={true}
              trend="stable"
              value={0}
              unit=" pts"
              decimals={0}
              onClick={() => setShowTrendModal(true)}
            />
          </div>
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">No mentions found</p>
            <p className="text-xs text-gray-400 mt-2">
              Your brand and your competitors were not found in the searched queries.
            </p>
          </div>
        </div>

        {/* Trend Modal */}
        <TrendModal
          isOpen={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title="Sentiment Score Trend"
          metricName="Sentiment Score"
          data={trendData}
          color="#10B981"
          unit=" pts"
        />
      </>
    )
  }

  // Get brands with data
  // Sort: Your brand first (if has mentions), then by sentiment score
  const brandsWithData = allSentimentData
    .filter((brand: any) => brand.totalMentions > 0)
    .sort((a: any, b: any) => {
      // Your brand always comes first
      if (a.isYourBrand) return -1
      if (b.isYourBrand) return 1
      // Otherwise sort by sentiment score
      return b.averageScore - a.averageScore
    })

  // Badge shows month-over-month change (current vs last month)
  const lastMonthScore = trendData[trendData.length - 2]?.value || score
  const monthOverMonthPoints = score - lastMonthScore

  const trend = score > lastMonthScore ? 'up' : score < lastMonthScore ? 'down' : 'stable'

  // Get the brand to display at the top:
  // - If your brand has mentions, show it
  // - Otherwise, show the top competitor
  const topBrand = (yourBrand && yourBrand.totalMentions > 0) ? yourBrand : brandsWithData[0]

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Smile className="h-5 w-5 text-green-600" />
            <h3 className="text-sm font-medium text-gray-600">Sentiment Analysis</h3>
            <Tooltip text="Measures how positively or negatively your brand is mentioned in AI responses. Scores range from 0-100, with higher scores indicating more favorable sentiment.">
              <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </Tooltip>
          </div>
          <TrendBadge
            hasHistoricalData={true}
            trend={trend}
            value={monthOverMonthPoints}
            unit=" pts"
            decimals={0}
            showPlusSign={true}
            onClick={() => setShowTrendModal(true)}
          />
        </div>

        {/* Top Brand Score */}
        <div className="mb-4">
          {yourBrand && yourBrand.totalMentions === 0 ? (
            // Your brand has no mentions - show N/A
            <>
              <div className="flex items-baseline space-x-2">
                <p className="text-3xl font-bold text-gray-400">N/A</p>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1">
                Can't determine sentitment with no mentions.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Your brand • 0 mentions
              </p>
            </>
          ) : (
            // Show actual sentiment data
            <>
              <div className="flex items-baseline space-x-2">
                <p className={`text-3xl font-bold ${getSentimentColor(topBrand.averageScore)}`}>
                  {topBrand.averageScore.toFixed(0)}
                </p>
                <span className="text-sm text-gray-500">/100</span>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1">
                {getSentimentLabel(topBrand.averageScore)}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {topBrand.isYourBrand ? 'Your brand' : topBrand.name} • {topBrand.totalMentions} mentions
              </p>
            </>
          )}
        </div>

        {/* Competitor Sentiment List */}
        <div className="space-y-3">
          {brandsWithData.slice(0, 3).map((brand: any) => (
            <div key={brand.id} className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${brand.isYourBrand ? 'text-gray-900' : 'text-gray-600'}`}>
                    {brand.name}
                    {brand.isYourBrand && <span className="ml-1 text-indigo-600 text-xs">(You)</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${getSentimentColor(brand.averageScore)}`}>
                    {brand.averageScore.toFixed(0)}
                  </span>
                  <span className="text-xs text-gray-400">{brand.totalMentions}m</span>
                </div>
              </div>

              {/* Mini sentiment breakdown */}
              <div className="grid grid-cols-3 gap-1">
                <div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-red-500 h-1 rounded-full transition-all"
                      style={{ width: `${brand.unfavorable}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-red-600">
                      <Frown className="h-2.5 w-2.5 inline mr-0.5" />
                    </span>
                    <span className="text-xs text-gray-500">{brand.unfavorable.toFixed(0)}%</span>
                  </div>
                </div>

                <div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-yellow-500 h-1 rounded-full transition-all"
                      style={{ width: `${brand.neutral}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-yellow-600">
                      <Meh className="h-2.5 w-2.5 inline mr-0.5" />
                    </span>
                    <span className="text-xs text-gray-500">{brand.neutral.toFixed(0)}%</span>
                  </div>
                </div>

                <div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-green-500 h-1 rounded-full transition-all"
                      style={{ width: `${brand.favorable}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-green-600">
                      <Smile className="h-2.5 w-2.5 inline mr-0.5" />
                    </span>
                    <span className="text-xs text-gray-500">{brand.favorable.toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trend Modal */}
      <TrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title="Sentiment Score Trend"
        metricName="Sentiment Score"
        data={trendData}
        color="#10B981"
        unit=" pts"
      />
    </div>
  )
}
